import autoSearchDirectory from './people/autoSearchDirectory';
import findPeopleCacheFirst from './people/findPeopleCacheFirst';
import { getSuggestionsService } from './people/getSuggestionsService';
import postProcessSuggestionsResult from './people/postProcessSuggestionsResult';
import processResponseResults from './people/processResponseResults';
import setIsSearching from './setIsSearching';
import showHideFindResult from './showHideFindResult';
import updateFindResultTransactionId from './updateFindResultTransactionId';
import datapoints from '../datapoints';
import ReadWriteRecipientWellStore from '../store/schema/ReadWriteRecipientWellStore';
import recipientWellStore from '../store/store';
import getPrioritizedResults from '../utils/getPrioritizedResults';
import tryGetRecipientsFromViewState from '../utils/tryGetRecipientsFromViewState';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { now } from 'owa-datetime';
import { isFeatureEnabled } from 'owa-feature-flags';
import updateCurrentRenderedQueryString from './updateCurrentRenderedQueryString';
import processCacheResult from 'owa-readwrite-recipient-well/lib/actions/people/processCacheResult';
import { getPeopleSuggestionsFromCache } from 'owa-recipient-cache/lib/actions/getPeopleSuggestionsFromCache';
import isCacheEmpty from 'owa-recipient-cache/lib/utils/isCacheEmpty';
import FindControlViewState, {
    DirectorySearchType,
} from 'owa-recipient-common/lib/store/schema/FindControlViewState';
import { FindResultType } from 'owa-recipient-common/lib/store/schema/FindControlViewState';
import ReadWriteRecipientViewState from 'owa-recipient-common/lib/store/schema/ReadWriteRecipientViewState';
import RecipientWellWithFindControlViewState from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import addTransactionComplete from 'owa-recipient-common/lib/utils/addTransactionComplete';
import addTransactionFailed from 'owa-recipient-common/lib/utils/addTransactionFailed';
import getPersonasFromReadWriteRecipientList from 'owa-recipient-common/lib/utils/getPersonasFromReadWriteRecipientList';
import shouldUse3SPeopleSuggestions from 'owa-substrate-people-suggestions/lib/utils/shouldUse3SPeopleSuggestions';
import { action, select } from 'satcheljs/lib/legacy';
import {
    resetFeedbackState,
    populateFeedbackStateNetwork,
    populateFeedbackStateCache,
} from '../utils/populateFeedbackState';

const MAX_ENTRIES_TYPEDOWN = 5;
const MAX_ENTRIES_DIRECTORY = 20;
const FIND_PEOPLE_TIMEOUT = 1000;
const FIND_PEOPLE_REQUEST_COUNT_THRESHOLD = 3;

interface TimeOutCheck {
    isTimedOut: boolean;
    isCacheEmpty: boolean;
}

let handleLongDelay = (
    recipientWell: FindControlViewState,
    recipients: ReadWriteRecipientViewState[],
    queryString: string,
    appendResults: boolean,
    resolveIfSingle: boolean,
    didTimeOut: TimeOutCheck
) => async () => {
    didTimeOut.isTimedOut = true;
    await searchCacheFallback(
        recipientWell,
        queryString,
        recipients,
        appendResults,
        resolveIfSingle,
        didTimeOut
    );
};

let searchCacheFallback = action('searchCacheFallback')(async function searchCacheFallback(
    recipientWell: FindControlViewState,
    queryString: string,
    recipients: ReadWriteRecipientViewState[],
    appendResults: boolean,
    resolveIfSingle: boolean,
    didTimeOut?: TimeOutCheck
): Promise<void> {
    if (recipientWell.queryString.trim() == queryString.trim()) {
        const transactionStartTime = now();
        let cacheResults = getPeopleSuggestionsFromCache(
            queryString,
            getPersonasFromReadWriteRecipientList(recipients)
        );

        populateFeedbackStateCache(
            recipientWell,
            transactionStartTime,
            cacheResults,
            true /*fallback*/
        );

        updateCurrentRenderedQueryString(recipientWell, queryString);
        recipientWell.directorySearchType = DirectorySearchType.None;

        if (cacheResults.length > 0) {
            processCacheResult(
                recipientWell,
                cacheResults,
                recipients,
                appendResults,
                resolveIfSingle
            );
        } else if (didTimeOut) {
            didTimeOut.isCacheEmpty = true;
        }
    }
});

export interface ReadWriteRecipientWellStoreState {
    store: ReadWriteRecipientWellStore;
}

export default action('findReadWriteRecipient')(
    select<ReadWriteRecipientWellStoreState>({
        store: () => recipientWellStore,
    })(async function findReadWriteRecipient(
        recipientWell: FindControlViewState | RecipientWellWithFindControlViewState,
        queryString: string,
        searchDirectory: boolean,
        resolveIfSingle?: boolean,
        recipientsToExclude?: ReadWriteRecipientViewState[],
        recipientsToPrioritize?: ReadWriteRecipientViewState[],
        searchCacheFirstOverride?: boolean,
        state?: ReadWriteRecipientWellStoreState
    ): Promise<void> {
        if (!searchDirectory) {
            // If this is searching directory we don't want to reset the feedback state since it should be a part of the same query
            resetFeedbackState(recipientWell);
        }

        let recipients = tryGetRecipientsFromViewState(recipientWell, recipientsToExclude);
        let appendResults = false;
        if (!recipientWell.shouldShowFindResult) {
            showHideFindResult(
                recipientWell,
                true /* shouldShowFindResult */,
                false /*shouldClearFindResult*/,
                recipients
            );
        }

        if (recipientsToPrioritize && recipientsToPrioritize.length > 0) {
            const prioritizedResults = getPrioritizedResults(queryString, recipientsToPrioritize);

            if (prioritizedResults && prioritizedResults.length > 0) {
                recipientWell.findResultSet = prioritizedResults;
                appendResults = true;
            }
        }

        // In the peopleSuggestionsCacheFirst flight, we query the cache first, backfill from the server and query AD if needed
        if (
            (searchCacheFirstOverride || isFeatureEnabled('rp-peopleSuggestionsCacheFirst')) &&
            !searchDirectory
        ) {
            await findPeopleCacheFirst(
                recipientWell,
                queryString,
                recipients,
                appendResults,
                resolveIfSingle
            );
            return;
        }

        let didAutoSearchDirectory = false;

        // If the pending FP request threshold has not been hit
        // Continue with normal find people call
        // Otherwise fallback to searching the cache
        if (state.store.pendingFindPeopleRequests < FIND_PEOPLE_REQUEST_COUNT_THRESHOLD) {
            let datapoint: PerformanceDatapoint = new PerformanceDatapoint(
                datapoints.SearchPeopleIndex.name
            );

            setIsSearching(recipientWell, true);

            /* If this is a Mailbox query, the current query returned 0 results, and the new query is additive, automatically issue a mailbox and directory request */
            const { currentRenderedQueryString, directorySearchType } = recipientWell;
            if (
                isFeatureEnabled('rp-optimizeAutoSearchDirectory') &&
                currentRenderedQueryString != '' &&
                !searchDirectory &&
                directorySearchType == DirectorySearchType.Auto &&
                currentRenderedQueryString.length < queryString.length &&
                queryString.indexOf(recipientWell.currentRenderedQueryString) == 0
            ) {
                await autoSearchDirectory(recipientWell, recipients, queryString, resolveIfSingle);
                didAutoSearchDirectory = true;
                datapoint.addCustomData([
                    false /*isTimedOut*/,
                    true /*isSameQueryString*/,
                    true /*searchDirectory*/,
                    false /*isCacheEmpty*/,
                    true /*didAutoSearchDirectory*/,
                    shouldUse3SPeopleSuggestions(),
                ]);
                datapoint.end();
            } else {
                let didTimeOut: TimeOutCheck = { isTimedOut: false, isCacheEmpty: false };
                let timeout =
                    searchDirectory || isCacheEmpty()
                        ? null
                        : setTimeout(
                              handleLongDelay(
                                  recipientWell,
                                  recipients,
                                  queryString,
                                  appendResults,
                                  resolveIfSingle,
                                  didTimeOut
                              ),
                              FIND_PEOPLE_TIMEOUT
                          );

                let transactionStartTime = now();
                try {
                    state.store.pendingFindPeopleRequests++;

                    let suggestionService = getSuggestionsService();

                    let currentRecipients: string[] = [];

                    // Add each recipient email addresses to the response
                    recipients.forEach(item => {
                        currentRecipients.push(item.persona.EmailAddress.EmailAddress);
                    });

                    // Record the beginning of the live call
                    // in case the live call hangs and the user submits feedback before it returns
                    populateFeedbackStateNetwork(
                        recipientWell,
                        transactionStartTime,
                        null /*response*/,
                        searchDirectory
                    );

                    let response = await suggestionService(
                        queryString,
                        searchDirectory,
                        isFeatureEnabled('cmp-implicitgroups'),
                        currentRecipients
                    );
                    state.store.pendingFindPeopleRequests--;
                    clearTimeout(timeout);

                    let sameQueryString = recipientWell.queryString == queryString;

                    populateFeedbackStateNetwork(
                        recipientWell,
                        transactionStartTime,
                        response,
                        searchDirectory
                    );

                    // Only update and show find result if:
                    // FP has not timed out or the cache results were empty
                    // And the query string hasn't been changed.
                    // If it has been changed, another findpeople call would have been issued
                    // and we can let its callback handle update and show find result.
                    if ((!didTimeOut.isTimedOut || didTimeOut.isCacheEmpty) && sameQueryString) {
                        updateCurrentRenderedQueryString(recipientWell, queryString);
                        recipientWell.directorySearchType = searchDirectory
                            ? DirectorySearchType.Manual
                            : DirectorySearchType.None;
                        let maxFindResultShown = searchDirectory
                            ? MAX_ENTRIES_DIRECTORY
                            : MAX_ENTRIES_TYPEDOWN;

                        processResponseResults(
                            recipientWell.findResultSet,
                            response,
                            appendResults,
                            maxFindResultShown,
                            searchDirectory /*returnMaskedRecipients*/
                        );

                        addTransactionComplete(recipientWell, transactionStartTime);

                        // If we got results, proceed as normal, otherwise check the cache before auto search the directory
                        if (recipientWell.findResultSet.length > 0) {
                            setIsSearching(recipientWell, false);
                            updateFindResultTransactionId(recipientWell, response);

                            let resultType = searchDirectory
                                ? FindResultType.SearchDirectory
                                : FindResultType.FindPeople;
                            postProcessSuggestionsResult(
                                recipientWell,
                                recipients,
                                resultType,
                                resolveIfSingle
                            );
                        } else {
                            // Check the local cache before falling back to the directory.
                            await searchCacheFallback(
                                recipientWell,
                                queryString,
                                recipients,
                                appendResults,
                                resolveIfSingle
                            );

                            if (recipientWell.findResultSet.length === 0) {
                                didAutoSearchDirectory = true;
                                await autoSearchDirectory(
                                    recipientWell,
                                    recipients,
                                    queryString,
                                    resolveIfSingle
                                );
                            }
                        }
                    }

                    datapoint.addCustomData([
                        didTimeOut.isTimedOut,
                        sameQueryString,
                        searchDirectory,
                        didTimeOut.isCacheEmpty,
                        didAutoSearchDirectory,
                        shouldUse3SPeopleSuggestions(),
                    ]);
                    datapoint.end();
                } catch (error) {
                    datapoint.endWithError(DatapointStatus.ServerError, error);
                    addTransactionFailed(recipientWell, transactionStartTime);
                    await searchCacheFallback(
                        recipientWell,
                        queryString,
                        recipients,
                        appendResults,
                        resolveIfSingle
                    );
                }
            }
        } else {
            let datapoint: PerformanceDatapoint = new PerformanceDatapoint(
                'FindPeopleThresholdHit'
            );
            await searchCacheFallback(
                recipientWell,
                queryString,
                recipients,
                appendResults,
                resolveIfSingle
            );
            datapoint.end();
        }

        setIsSearching(recipientWell, false);
    })
);
